// #region imports

// --- Angular
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormControl,
  Validators,
} from '@angular/forms';

// --- RxJs
import { BehaviorSubject } from 'rxjs';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import {
  Constants,
  GroupModel,
  LanguageModel,
  RespondentEmailValidationModel,
  RespondentModel,
  RespondentStatuses,
  SelectListItem,
} from '../../../models';
import {
  GenericValidator,
  RangeValidator,
} from '../../../shared';

// #endregion

@Component({
  selector: 'hs-manage-respondents-add-respondent',
  templateUrl: './manage-respondents-add-respondent.component.html',
  styleUrls: ['./manage-respondents-add-respondent.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageRespondentsAddRespondentComponent implements OnInit, OnChanges {
  @Input() groups: GroupModel[] | null = new Array<GroupModel>();
  @Input() selectedGroupId: string | null = '';
  @Input() validationErrorMessage: string | null = '';
  @Input() validatedRespondent: RespondentEmailValidationModel | null = null;

  @Input() validationMessageFirstNameRequired: string;
  @Input() validationMessageFirstNameMaxLength: string;
  @Input() validationMessageLastNameRequired: string;
  @Input() validationMessageLastNameMaxLength: string;
  @Input() validationMessageEmailAddressRequired: string;
  @Input() validationMessageEmailAddressMaxLength: string;
  @Input() validationMessageEmailAddressInvalid: string;
  @Input() validationMessageLanguageRequired: string;
  @Input() validationMessagePersonalMessageMaxLength: string;
  @Input() validationMessageGroupRequired: string;
  @Input() languages: LanguageModel[] | null = new Array<LanguageModel>();

  @Output() dismissModalClick = new EventEmitter();
  @Output() backButtonClick = new EventEmitter();
  @Output() addRespondentClick = new EventEmitter();
  @Output() emailAddressChanged =
    new EventEmitter<RespondentEmailValidationModel>();
  @Output() respondentAdded = new EventEmitter<RespondentModel>();

  canAdd = false;
  isValidatingEmailAddress: boolean = false;
  isValid: boolean = false;
  respondentForm: FormGroup;
  displayMessage: { [key: string]: string } = {};
  error: string = '';

  localLanguages$: BehaviorSubject<SelectListItem[]>;
  private minLanguageId = 1;
  private maxLanguageId = 30;

  private validationMessages: { [key: string]: { [key: string]: string } } = {};
  private genericValidator: GenericValidator | undefined;

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService
  ) {
    this.localLanguages$ = new BehaviorSubject<SelectListItem[]>([
      {
        text: this.translateService.instant('commonTextLoading'),
        value: '',
        isSelected: true,
      } as SelectListItem,
    ]);

    this.validationMessageFirstNameRequired =
      this.translateService.instant('firstNameRequired');
    this.validationMessageFirstNameMaxLength =
      this.translateService.instant('firstNameExceed100');
    this.validationMessageLastNameRequired =
      this.translateService.instant('lastNameRequired');
    this.validationMessageLastNameMaxLength =
      this.translateService.instant('lastNameExceed100');
    this.validationMessageEmailAddressRequired = this.translateService.instant(
      'emailAddressRequired'
    );
    this.validationMessageEmailAddressMaxLength = this.translateService.instant(
      'emailAddressExceed256'
    );
    this.validationMessageEmailAddressInvalid = this.translateService.instant(
      'supplyValidEmailAddress'
    );
    this.validationMessagePersonalMessageMaxLength =
      this.translateService.instant('personalMessageExceed2000');
    this.validationMessageLanguageRequired =
      this.translateService.instant('languageRequired');
    this.validationMessageGroupRequired =
      this.translateService.instant('groupRequired');

    this.respondentForm = this.formBuilder.group({
      firstName: new FormControl('', [
        Validators.required,
        Validators.maxLength(Constants.FIRST_NAME_MAXIMUM_LENGTH),
      ]),
      lastName: new FormControl('', [
        Validators.required,
        Validators.maxLength(Constants.LAST_NAME_MAXIMUM_LENGTH),
      ]),
      emailAddress: new FormControl('', [
        Validators.required,
        Validators.maxLength(Constants.EMAIL_ADDRESS_MAXIMUM_LENGTH),
        Validators.email,
      ]),
      language: new FormControl({ value: 0, disabled: false }, [
        Validators.required,
        RangeValidator(this.minLanguageId, this.maxLanguageId),
      ]),
      group: new FormControl('', [Validators.required]),
      personalMessage: new FormControl('', [
        Validators.maxLength(
          Constants.INVITE_RESPONDENTS_PERSONAL_MESSAGE_MAX_LENGTH
        ),
      ]),
    });
  }

  ngOnInit() {
    this.validationMessages = {
      firstName: {
        required: this.validationMessageFirstNameRequired,
        maxlength: this.validationMessageFirstNameMaxLength,
      },
      lastName: {
        required: this.validationMessageLastNameRequired,
        maxlength: this.validationMessageLastNameMaxLength,
      },
      emailAddress: {
        required: this.validationMessageEmailAddressRequired,
        maxlength: this.validationMessageEmailAddressMaxLength,
        email: this.validationMessageEmailAddressInvalid,
      },
      language: {
        required: this.validationMessageLanguageRequired,
        rangeValidator: this.validationMessageLanguageRequired,
      },
      personalMessage: {
        maxlength: this.validationMessagePersonalMessageMaxLength,
      },
      group: {
        required: this.validationMessageGroupRequired,
      },
    };

    this.genericValidator = new GenericValidator(this.validationMessages);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['languages'] && changes['languages'].currentValue) {
      const localLanguages = new Array<SelectListItem>();
      for (const lang of changes['languages'].currentValue) {
        const item = {
          isDisabled: false,
          isSelected:
            this.validatedRespondent &&
            lang.id === this.validatedRespondent.languageId,
          text: lang.nativeName,
          value: lang.id,
        } as SelectListItem;
        localLanguages.push(item);
      }
      this.minLanguageId = Math.min(
        ...changes['languages'].currentValue.map((l: LanguageModel) => l.id)
      );
      this.maxLanguageId = Math.max(
        ...changes['languages'].currentValue.map((l: LanguageModel) => l.id)
      );
      this.localLanguages$.next(localLanguages);
    }

    if (
      changes &&
      changes['validatedRespondent'] &&
      changes['validatedRespondent'].currentValue &&
      this.respondentForm
    ) {
      this.isValidatingEmailAddress = false;
      this.isValid = this.validatedRespondent?.isValid ?? false;

      this.respondentForm
        .get('firstName')
        ?.setValue(this.validatedRespondent!.firstName);
      this.respondentForm
        .get('lastName')
        ?.setValue(this.validatedRespondent!.lastName);
      this.respondentForm
        .get('language')
        ?.setValue(this.validatedRespondent!.languageId);

      this.error = this.validatedRespondent!.errorMessage;
      if (!this.validatedRespondent!.isValid) {
        this.respondentForm.setErrors({ invalid: true });
      } else {
        this.respondentForm.setErrors({});
        this.displayMessage = {};
        this.respondentForm.updateValueAndValidity();
      }
    }

    if (
      changes &&
      changes['selectedGroupId'] &&
      changes['selectedGroupId'].currentValue &&
      this.respondentForm
    ) {
      this.respondentForm
        .get('group')
        ?.setValue(this.selectedGroupId!.toLowerCase());
    }

    if (this.respondentForm) {
      this.canAdd = this.respondentForm.valid;
    }
  }

  onDismissModalClicked(): void {
    this.dismissModalClick.emit();
  }

  onBackButtonClicked(): void {
    this.backButtonClick.emit();
  }

  onEmailAddressBlur(e: any): void {
    e.stopPropagation();
    let groupId: string = this.respondentForm?.get('group')?.value;
    if (
      !groupId ||
      (groupId.length === 0 && this.groups && this.groups.length > 0)
    ) {
      groupId = this.groups![0]!.id;
    }
    if (
      this.respondentForm!.get('emailAddress')!.valid &&
      this.respondentForm!.get('emailAddress')!.dirty
    ) {
      // this.respondentForm.disable();
      this.respondentForm!.get('firstName')!.setValue('');
      this.respondentForm!.get('lastName')!.setValue('');
      this.respondentForm!.get('personalMessage')!.setValue('');
      this.isValidatingEmailAddress = true;
      this.emailAddressChanged.emit({
        emailAddress: this.respondentForm!.get('emailAddress')!.value,
        respondentGroupIndex: 1,
        groupId,
        onlyCheckBounced: true,
      } as RespondentEmailValidationModel);
    }
  }

  onBlur(): void {
    this.displayMessage = this.genericValidator?.processMessages(
      this.respondentForm!,
      true
    ) ?? {};
    this.canAdd = this.respondentForm!.valid ?? false;
  }

  onMessageChanged(): void {
    this.displayMessage = this.genericValidator?.processMessages(
      this.respondentForm!,
      true
    ) ?? {};
    this.canAdd = this.respondentForm?.valid ?? false;
  }

  onGroupChanged(groupId: string): void {
    this.displayMessage = this.genericValidator?.processMessages(
      this.respondentForm!,
      true
    ) ?? {};
    this.canAdd = this.respondentForm?.valid ?? false;
  }

  onGroupFocus(): void {}

  onSubmit(): void {
    if (!this.isValid) {
      return;
    }

    this.displayMessage = this.genericValidator?.processMessages(
      this.respondentForm!,
      true
    ) ?? {};

    if (!this.respondentForm?.valid ?? false) {
      return;
    }

    if (!this.respondentForm.dirty) {
      return;
    }

    const form = { ...this.respondentForm.value };
    const respondent = {} as RespondentModel;
    respondent.isSaving = true;
    respondent.firstName = form.firstName;
    respondent.lastName = form.lastName;
    respondent.emailAddress = form.emailAddress;
    respondent.groupId = form.group;
    respondent.personalMessage = form.personalMessage;
    respondent.percentageComplete = 0;
    respondent.languageId = form.language;
    respondent.isSet = true;
    respondent.isValid = true;
    respondent.status = RespondentStatuses.added;
    respondent.avatar = this.validatedRespondent
      ? this.validatedRespondent.avatar
      : '';

    this.respondentAdded.emit(respondent);
  }
}
